import { takeEvery, select } from 'redux-saga/effects';
import { Api, ClientApi } from 'plappermaul-shared';

// Auth
import {
  login as loginAction,
  externalLoginAction,
  signup as signupAction,
  loadJobs as loadJobsAction,
  resetPassword as resetPasswordAction,
  requestResetPassword,
  loadUserInfo as loadInfoAction,
  onChangePassword as changePasswordAction,
  onChangeUserData as changeUserInfoAction,
  signupMultiple as signupMultipleAction,
  onLoadConfiguration,
  authorizeAction,
  loadUserGroupsForUserAction,
  loadUserRolesAction,
  platformSignup as platformSignupAction,
  checkOrganizationNameAvailableAction,
} from '../reducers/auth.reducers';

import {
  login,
  signup,
  platformSignup,
  refreshAccessToken,
  loadJobs,
  resetPassword,
  requestPasswordReset,
  loadUserInfo,
  changeUserPassword,
  changeUserData,
  registerMultiple,
  loadConfigurationForBrand,
  externalLogin,
  authorize,
  loadUserGroups,
  loadUserRoles,
  checkOrganizationNameIsAvialable,
} from './auth.sagas';

// courses
import {
  onLoadCourses,
  onCardSwiped,
  onFinishRun,
  onRestart,
  onLoadCourse,
  onLoadCard,
  onSearch,
  onExportContent,
  loadDefaultBulletinBoard as loadDefaultBulletinBoardAction,
  getPinnedContentAction,
  pinOrUnpinContentAction,
  loadCardsForBulletinBoardAction,
 } from '../reducers/course.reducers';

import {
  loadCourses,
  loadCourseCards,
  sendRunAction,
  finishRun,
  resetProgress,
  loadCourseCard,
  search,
  exportPdf,
  loadDefaultBulletinBoard,
  getPinnedContent,
  pinOrUnpinContent,
  getCardsForBulletinBoard,
 } from './course.sagas';


// tests
import {
  onLoadTests,
  selectTest,
  onSubmitTest,
  onLoadResults,
  selectResult,
  onStartTest,
  onSubmitAnswer,
  onLoadExams,
  selectExam,
  onSubmitExamAnswer,
  onLoadAllTestQuestions,
  loadSingleQuestion,
  onRemoveTestRun,
} from '../reducers/test.reducers';

import {
  loadTests,
  loadResults,
  loadTestQuestions,
  loadResultQuestions,
  submitTestRun,
  startTest,
  submitAnswer,
  loadExamTests,
  loadExamQuestions,
  submitExamAnswer,
  loadAllTestQuestions,
  loadQuestionById,
  removeTestRun,
} from './test.sagas';

import {
  loadSocialPost,
  loadSocialWallPosts,
  createSocialWallPost,
  createSocialWallResponse,
  removeImageForSocialPost,
  removeSocialWallPost,
  updateSocialWallPost,
} from './socialwall.sagas';

import {
  loadSocialWallPostAction,
  loadMoreSocialWallPostsAction,
  loadSocialWallPostsAction,
  createSocialWallPostAction,
  removeImageForSocialPostAction,
  createResponseAction,
  updateSocialWallPostAction,
  deleteSocialWallPostAction,
} from '../reducers/socialwall.reducers';

import {
  loadNotificationCenterEntriesAction,
  updateNotificationEntryStateAction,
} from '../reducers/notification.reducers';

import {
  updateNotificationCenterEntryStates,
  loadNotificationCenterEntries,
} from './notification.sagas';

import config from '../../run-config';

const { AuthorizationApi } = Api;
const { CourseApi, TestApi, UserApi, AppApi, SocialWallApi, NotificationApi } = ClientApi;

export default function* rootSaga() {
  function* tokenProvider() {
    const { accessToken, refreshToken, expiresAt } = yield select(state => state.authentication);
    return {
      token: accessToken,
      refreshToken,
      expiresAt,
    };
  }

  function* tokenRefreshTask() {
    return yield refreshAccessToken(authApi);
  }

  const authApi = new AuthorizationApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const courseApi = new CourseApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const testApi = new TestApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const userApi = new UserApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const appApi = new AppApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const socialApi = new SocialWallApi(config.apiHost, tokenProvider, tokenRefreshTask);
  const notificationApi = new NotificationApi(config.apiHost, tokenProvider, tokenRefreshTask);

  yield [
    // auth
    takeEvery(onLoadConfiguration, loadConfigurationForBrand, appApi),
    takeEvery(loginAction, login, authApi),
    takeEvery(signupAction, signup, userApi),
    takeEvery(loadJobsAction, loadJobs, userApi),
    takeEvery(resetPasswordAction, resetPassword, userApi),
    takeEvery(requestResetPassword, requestPasswordReset, userApi),
    takeEvery(loadInfoAction, loadUserInfo, userApi),
    takeEvery(changePasswordAction, changeUserPassword, userApi),
    takeEvery(changeUserInfoAction, changeUserData, userApi),
    takeEvery(signupMultipleAction, registerMultiple, userApi),
    takeEvery(externalLoginAction, externalLogin, authApi),
    takeEvery(authorizeAction, authorize, authApi),
    takeEvery(loadUserGroupsForUserAction, loadUserGroups, userApi),
    takeEvery(loadUserRolesAction, loadUserRoles, authApi),
    takeEvery(platformSignupAction, platformSignup, userApi),
    takeEvery(checkOrganizationNameAvailableAction, checkOrganizationNameIsAvialable, userApi),

    // courses
    takeEvery(onLoadCourses, loadCourses, courseApi),
    takeEvery(onLoadCard, loadCourseCard, courseApi),
    takeEvery(onSearch, search, courseApi),

    takeEvery(onLoadCourse, loadCourseCards, courseApi),
    takeEvery(onCardSwiped, sendRunAction, courseApi),
    takeEvery(onFinishRun, finishRun, courseApi),
    takeEvery(onRestart, resetProgress, courseApi),
    takeEvery(loadCardsForBulletinBoardAction, getCardsForBulletinBoard, courseApi),

    // bulletin board
    takeEvery(loadDefaultBulletinBoardAction, loadDefaultBulletinBoard, courseApi),
    takeEvery(getPinnedContentAction, getPinnedContent, courseApi),
    takeEvery(pinOrUnpinContentAction, pinOrUnpinContent, courseApi),

    // tests
    takeEvery(onLoadTests, loadTests, testApi),
    takeEvery(selectTest, loadTestQuestions, testApi),
    // takeEvery(onSubmitTest, submitTestRun, testApi),
    takeEvery(onStartTest, startTest, testApi),
    takeEvery(onSubmitAnswer, submitAnswer, testApi),
    takeEvery(onLoadExams, loadExamTests, testApi),
    takeEvery(selectExam, loadExamQuestions, testApi),
    takeEvery(onSubmitExamAnswer, submitExamAnswer, testApi),
    takeEvery(onLoadAllTestQuestions, loadAllTestQuestions, testApi),
    takeEvery(loadSingleQuestion, loadQuestionById, testApi),
    takeEvery(onRemoveTestRun, removeTestRun, testApi),

    // results
    takeEvery(onLoadResults, loadResults, testApi),
    takeEvery(selectResult, loadResultQuestions, testApi),

    // socialwall
    takeEvery(loadSocialWallPostAction, loadSocialPost, socialApi),
    takeEvery(loadSocialWallPostsAction, loadSocialWallPosts, socialApi),
    takeEvery(loadMoreSocialWallPostsAction, loadSocialWallPosts, socialApi),
    takeEvery(removeImageForSocialPostAction, removeImageForSocialPost, socialApi),
    takeEvery(createSocialWallPostAction, createSocialWallPost, socialApi),
    takeEvery(createResponseAction, createSocialWallResponse, socialApi),
    takeEvery(updateSocialWallPostAction, updateSocialWallPost, socialApi),
    takeEvery(deleteSocialWallPostAction, removeSocialWallPost, socialApi),

    // notifications
    takeEvery(loadNotificationCenterEntriesAction, loadNotificationCenterEntries, notificationApi),
    takeEvery(updateNotificationEntryStateAction, updateNotificationCenterEntryStates, notificationApi),

    // export
    takeEvery(onExportContent, exportPdf, courseApi),
  ];
}



// WEBPACK FOOTER //
// ./src/app/sagas/index.js